import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Monogram } from '@/components/generals/monogram'
import { COMMISSION_HREF, NAV_ITEMS } from '@/constants/navigation'

const SiteHeader: FC = (): ReactNode => {
    const [scrolled, setScrolled] = useState(false)
    const [open, setOpen] = useState(false)

    useEffect(() => {
        const onScroll = (): void => setScrolled(window.scrollY > 24)
        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })
        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        if (!open) return
        const onKey = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') setOpen(false)
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [open])

    const close = (): void => setOpen(false)

    return (
        <header
            className={cn(
                'fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-300',
                scrolled || open ? 'border-b border-white/5 bg-obsidian/85 backdrop-blur-md' : 'border-b border-transparent bg-obsidian/40 backdrop-blur-[2px]'
            )}
        >
            <div className='mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8'>
                <a href='#film' onClick={close} className='flex items-center gap-3 rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold' aria-label='Alif Lamato, back to the film'>
                    <Monogram className='size-8' />
                    <span className='font-heading text-[12px] font-bold tracking-[0.2em] text-white sm:text-[13px]'>
                        ALIF LAMATO
                    </span>
                </a>

                <nav aria-label='Main' className='hidden items-center gap-9 md:flex'>
                    {NAV_ITEMS.map((item) => (
                        <a
                            key={item.href}
                            href={item.href}
                            className='font-mono text-[11px] uppercase tracking-[0.24em] text-titanium-light transition-colors hover:text-gold focus-visible:text-gold focus-visible:outline-none'
                        >
                            {item.label}
                        </a>
                    ))}
                </nav>

                <div className='flex items-center gap-2'>
                    <a
                        href={COMMISSION_HREF}
                        className='hidden rounded-sm border border-gold/70 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.24em] text-gold transition-colors hover:bg-gold hover:text-obsidian focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold sm:inline-flex'
                    >
                        Commission
                    </a>
                    <button
                        type='button'
                        onClick={() => setOpen((value) => !value)}
                        aria-expanded={open}
                        aria-controls='mobile-menu'
                        aria-label={open ? 'Close menu' : 'Open menu'}
                        className='flex size-10 items-center justify-center rounded-sm text-white transition-colors hover:text-gold focus-visible:outline-2 focus-visible:outline-gold md:hidden'
                    >
                        {open ? <X className='size-5' /> : <Menu className='size-5' />}
                    </button>
                </div>
            </div>

            {open && (
                <nav id='mobile-menu' aria-label='Mobile' className='border-t border-white/5 bg-obsidian/95 px-5 pb-8 pt-4 md:hidden'>
                    <ul className='flex flex-col'>
                        {NAV_ITEMS.map((item) => (
                            <li key={item.href}>
                                <a href={item.href} onClick={close} className='block border-b border-white/5 py-4 font-heading text-sm font-bold tracking-[0.18em] text-white transition-colors hover:text-gold'>
                                    {item.label.toUpperCase()}
                                </a>
                            </li>
                        ))}
                    </ul>
                    <a href={COMMISSION_HREF} onClick={close} className='mt-6 flex items-center justify-center rounded-sm bg-gold px-5 py-3.5 font-mono text-xs font-semibold uppercase tracking-[0.24em] text-obsidian'>
                        Commission a set
                    </a>
                </nav>
            )}
        </header>
    )
}

export default SiteHeader
